import React, {useEffect} from "react"
import {Application} from "../../resources/application"
import {Meta, Resource} from "../../resources/common"
import {AllowedApplicationsColumns, ApplicationsColumns} from "./Application.types"
import {useIsOrgUser} from "../../services/auth"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import {isProdEnv} from "../../utilities/environment"
import ReactTooltip from "react-tooltip"
import Icon from "../Icon/Icon"
import ApplicationRow from "./ApplicationRow"
import PagingNavBar from "../PagingNavBar/PagingNavBar"

interface ApplicationsTableProps {
	applications: Array<Application>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	include?: Resource[]
	includedColumns: Array<AllowedApplicationsColumns>
	fullHeight?: boolean
	showTabsOnHeader?: boolean
	meta?: Meta
	isFiltering?: boolean
	sortFunction: () => void
	sortBy: string
}

export default function ApplicationsTable({
	applications,
	hasResults,
	hasPrev,
	hasNext,
	prev,
	next,
	isUsingPaging,
	include,
	includedColumns,
	fullHeight,
	showTabsOnHeader,
	meta,
	isFiltering,
	sortFunction,
	sortBy,
}: ApplicationsTableProps) {
	const isOrgUser = useIsOrgUser()
	const noContent = applications.length === 0
	const tutorialCallButton = document.querySelector(".tutorial-call-button")

	useEffect(() => {
		if (!noContent && tutorialCallButton) {
			tutorialCallButton.classList.add("tutorial-call-button-with-animation")
		}
	}, [])

	return (
		<div className={"applications-table"}>
			<DataTable
				isEmpty={noContent}
				fullHeight={fullHeight}
				stickyAction={fullHeight}
				stickyActionWithTabsOnHeader={fullHeight && showTabsOnHeader}
				noContentText={
					<>
						{!isFiltering ? (
							<>
								No customer{" "}
								<a className="doc-link" href="https://docs.unit.co/#applications" target="_blank" rel="noreferrer">
									applications
								</a>{" "}
								yet
							</>
						) : (
							"No customer applications found"
						)}
					</>
				}
				noContentCtaButton={
					!isProdEnv() &&
					!isFiltering &&
					isOrgUser && (
						<button
							className="tutorial-cta-button"
							data-tracking-label="tutorial-applications-cta"
							onClick={(e) => {
								const target = e.target as HTMLButtonElement
								target.classList.add("tutorial-cta-button-was-used")
								if (tutorialCallButton) {
									ReactTooltip.show(tutorialCallButton)
								}
							}}
						>
							<Icon icon={"programming-code--programing-apps-websites"} color="white" />
							Test Create Application API
						</button>
					)
				}
			>
				<DataTableHead>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === ApplicationsColumns.createdAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction()}
										key={column[1]}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{applications.map((application) => (
						<ApplicationRow
							key={application.id}
							application={application}
							includedColumns={includedColumns}
							include={include}
						/>
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
				meta={meta}
			/>
		</div>
	)
}
